import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, LineChart, BarChart, AreaChart, Area, Bar, Line, Cell, XAxis, YAxis, Tooltip, ReferenceLine
} from 'recharts';
import {
  fetchMachineWidget, fetchMachineTemperature, fetchMachineCycle, fetchMachineSpc, fetchMachineQualitySummary,
  fetchCustomWidget, fetchCustomWidgetData, fetchEnergySummary, fetchShotHistory
} from '../../../services/api';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useTranslation } from '../../../context/LanguageContext';
import './DashboardWidgets.css';

/* ===== 대시보드 위젯 본문 — PNG 시안「사출기 #1 _ 대시보드 _ 보기 모드」의 위젯 8종 =====
 * 위젯마다 자기 데이터를 직접 불러오고(useAutoRefresh 로 시스템 설정 주기 갱신) 스타일은 wg- 접두어만 쓴다.
 * 사용자 모듈은 Machine.jsx 의 빌더·renderWidget 을 그대로 쓴다(WidgetDashboard 가 분기).
 *
 * 데이터 출처 (모두 기존 API) — 시안에 있지만 백엔드에 없는 값은 만들지 않는다
 * - 기본정보    : /widget/basic-info — 제품명·샷카운트·사이클타임·캐비티. 시안의 "금형" 은 API 에 없어 뺐다
 * - 온도        : /dashboard/machine/{id}/temperature — Zone별 현재/설정/상태 + 최근 추이(미니 막대). 경고 Zone 은 테두리·배지
 * - 에너지      : /widget/energy-summary — 오늘 kWh(설비 현재 상태 energy_consumption_today) · 효율(kWh/샷 = 오늘 kWh ÷ 오늘 샷) · 최근 7샷 에너지 막대
 *                시안의 "전일 대비" 배지는 설비별 일별 이력이 없어 두지 않는다
 * - 알람이력    : /widget/alarm-history — 최근 알람(심각도 색 막대 + 메시지 + 시각)
 * - 압력        : /widget/pressure — 샷별 사출/보압(MPa). 시안 범례 "실측/설정" 은 설정 계열이 없어 "사출/보압"
 * - 사이클      : /dashboard/machine/{id}/cycle — 평균·최소·최대 + 최근 샷 사이클타임 막대
 * - SPC         : /dashboard/machine/{id}/spc?parameter=cushion — X̄ 관리도(UCL/LCL·이탈). 쿠션량 설정이 없으면 사이클타임으로 대체
 * - 양/불       : /machine/quality-summary/{id} — 오늘 양품률·양품·불량·불량률
 * - 사용자모듈  : /widget/custom-data?yAxis= — 빌더 설정(chartType · xAxis · yAxis)대로. 압력은 사출/보압/계량/배압 4계열, 온도는 Zone 5계열,
 *                속도·위치는 단일 계열. 위젯 설정 저장이 행을 다시 만들어 id 가 바뀌므로 id 대신 yAxis 로 조회하고,
 *                백엔드 배포 전(404)에는 /widget/custom/{widgetId} 로 대체한다
 * 값이 null 이면 '—'. */

const isNum = (v) => v != null && !Number.isNaN(Number(v));
const num = (v, d = 1) => (isNum(v) ? Number(v).toFixed(d) : '—');
const intFmt = (v) => (isNum(v) ? Number(v).toLocaleString() : '—');
const fmtTime = (v) => { const m = String(v || '').match(/[T ](\d{2}:\d{2})/); return m ? m[1] : '—'; };

/* 토큰 (Machine.css --smi-* 와 같은 값. recharts 는 CSS 변수를 못 받는다) */
const C = {
  primary: '#FFFFFF', secondary: '#C5D7E0', tertiary: '#98A8BF', muted: '#798699',
  accent: '#83A6FF', success: '#47CD89', warning: '#FDB022', error: '#F04438',
  bar: '#98A8BF', grid: 'rgba(255,255,255,0.06)', tooltipBg: '#1A1E24', tooltipBorder: 'rgba(255,255,255,0.12)'
};
const TOOLTIP = {
  contentStyle: { backgroundColor: C.tooltipBg, border: `1px solid ${C.tooltipBorder}`, borderRadius: '4px', fontSize: '12px' },
  labelStyle: { color: C.tertiary, fontSize: '12px' },
  itemStyle: { color: C.primary }
};
const TICK = { fill: C.tertiary, fontSize: 10 };

/* 위젯 공통 로더 — 자동 갱신은 조용히(silent) 다시 불러온다 */
function useWidgetData(fetcher, equipmentId) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | error
  const load = useCallback(async (silent) => {
    if (!equipmentId) return;
    if (!silent) setState('loading');
    try {
      const d = await fetcher(equipmentId);
      setData(d);
      setState('ok');
    } catch (e) {
      console.error('Dashboard widget load failed:', e);
      if (!silent) setState('error');
    }
  }, [fetcher, equipmentId]);
  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true), !!equipmentId);
  return { data, state };
}

function Message({ text }) { return <div className="wg-message">{text}</div>; }

function Frame({ state, tr, children, empty }) {
  if (state === 'loading') return <Message text={tr('loading')} />;
  if (state === 'error') return <Message text={tr('loadFailed')} />;
  if (empty) return <Message text={tr('noData')} />;
  return <div className="wg-root">{children}</div>;
}

/* ---------------- 기본정보 ---------------- */
const fetchBasic = (id) => fetchMachineWidget(id, '기본정보');
function BasicInfoWidget({ equipmentId, t, tr }) {
  const { data, state } = useWidgetData(fetchBasic, equipmentId);
  const b = data?.basicInfo || null;
  const L = (k) => t(`pages.monitoring.machine.overview.labels.${k}`);
  return (
    <Frame state={state} tr={tr} empty={!b}>
      <dl className="wg-rows">
        <div><dt>{L('productName')}</dt><dd>{b?.productName || '—'}</dd></div>                               {/* basicInfo.productName */}
        <div><dt>{L('shotCount')}</dt><dd>{intFmt(b?.shotCount)}</dd></div>                                   {/* basicInfo.shotCount */}
        <div><dt>{L('cycleTime')}</dt><dd>{num(b?.cycleTime)}<small>s</small></dd></div>                       {/* basicInfo.cycleTime */}
        <div><dt>{L('cavityCount')}</dt><dd>{intFmt(b?.cavityCount)}<small>EA</small></dd></div>              {/* basicInfo.cavityCount */}
      </dl>
    </Frame>
  );
}

/* ---------------- 온도 ---------------- */
function TemperatureWidget({ equipmentId, tr, size }) {
  const { data, state } = useWidgetData(fetchMachineTemperature, equipmentId);
  const zones = data?.zoneHeaters || [];
  const trend = data?.tempTrend?.data || [];
  const compact = size === '1x1' || size === '1x2';
  const shown = compact ? zones.slice(0, 4) : zones.slice(0, 5);
  const bars = (idx) => trend.slice(-10).map((row) => row?.[`zone${idx + 1}`]).filter(isNum).map(Number);
  return (
    <Frame state={state} tr={tr} empty={zones.length === 0}>
      <div className={`wg-zones ${compact ? 'wg-zones-compact' : ''}`}>
        {shown.map((z, i) => {
          const warn = (z.status || '').toLowerCase() === 'warning';
          const vals = bars(i);
          const max = Math.max(1, ...vals);
          const min = Math.min(...vals);
          return (
            <div key={z.zoneName || i} className={`wg-zone ${warn ? 'wg-zone-warn' : ''}`}>
              <div className="wg-zone-head">
                <span className="wg-zone-name">{z.zoneName || `Zone ${i + 1}`}</span>
                {warn && <span className="wg-badge wg-badge-warn">{tr('warning')}</span>}
              </div>
              <div className={`wg-zone-value ${warn ? 'wg-zone-value-warn' : ''}`}>{num(z.currentTemp)}<small>℃</small></div>   {/* zoneHeaters[].currentTemp */}
              <div className="wg-zone-set">{tr('set')} {num(z.setTemp, 0)}℃</div>                                                 {/* zoneHeaters[].setTemp */}
              <div className="wg-bars">
                {vals.map((v, k) => (
                  <i key={k} className={warn && k === vals.length - 1 ? 'wg-bar-warn' : ''} style={{ height: `${max === min ? 60 : 30 + ((v - min) / (max - min)) * 70}%` }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

/* ---------------- 에너지 (시안: 오늘 kWh 큰 수치 · 효율 kWh/샷 · 최근 샷 막대, 마지막 막대 액센트) ---------------- */
/* 요약 API(energy-summary)가 아직 배포되지 않은 백엔드(404)에서는 기존 샷별 에너지 API 로 막대만 그리고 오늘 kWh·효율은 '—' */
const fetchEnergy = async (id) => {
  try {
    return await fetchEnergySummary(id, 7);
  } catch (e) {
    const legacy = await fetchMachineWidget(id, '에너지');
    return { energySummary: { todayKwh: null, todayShots: null, kwhPerShot: null, recent: (legacy?.data || []).slice(0, 7) }, legacy: true };
  }
};
function EnergyWidget({ equipmentId, tr }) {
  const { data, state } = useWidgetData(fetchEnergy, equipmentId);
  const s = data?.energySummary || null;
  /* 전력량이 안 들어오는 설비(화성 등)도 샷 막대는 그린다 — 값이 없으면 0 으로 보고 최소 높이로 표시 */
  const recent = useMemo(() => [...(s?.recent || [])].reverse().map((r) => ({ ...r, power: isNum(r.power) ? Number(r.power) : 0 })), [s]);
  const max = Math.max(1, ...recent.map((r) => Number(r.power)));
  return (
    <Frame state={state} tr={tr} empty={!s}>
      <div className="wg-kpi">
        <span className="wg-kpi-value">{Number(isNum(s?.todayKwh) ? s.todayKwh : 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}<small>kWh · {tr('today')}</small></span>   {/* energySummary.todayKwh (미수집 = 0) */}
      </div>
      <div className="wg-kpi-sub">{tr('efficiency')} <b>{num(isNum(s?.kwhPerShot) ? s.kwhPerShot : 0, 2)} kWh/{tr('shot')}</b></div>   {/* energySummary.kwhPerShot (미수집 = 0) */}
      <div className="wg-bars wg-bars-lg">
        {recent.map((r, k) => (
          <i key={r.shotNumber ?? k} className={k === recent.length - 1 ? 'wg-bar-accent' : ''} style={{ height: `${Math.max(8, (Number(r.power) / max) * 100)}%` }} title={`#${r.shotNumber ?? ''} ${num(r.power, 2)} kWh`} />
        ))}
      </div>
    </Frame>
  );
}

/* ---------------- 알람이력 ---------------- */
const fetchAlarms = (id) => fetchMachineWidget(id, '알람이력');
const severityTone = (s) => {
  const v = (s || '').toUpperCase();
  if (v === 'CRITICAL' || v === 'HIGH' || v === 'ERROR') return 'error';
  if (v === 'WARNING' || v === 'MEDIUM') return 'warning';
  return 'info';
};
function AlarmHistoryWidget({ equipmentId, tr, size }) {
  const { data, state } = useWidgetData(fetchAlarms, equipmentId);
  const list = (data?.alarmHistory || []).slice(0, size === '1x2' || size === '2x2' ? 10 : 5);
  return (
    <Frame state={state} tr={tr} empty={list.length === 0}>
      <ul className="wg-alarms">
        {list.map((a, i) => (
          <li key={`${a.time}-${i}`} className={`wg-alarm wg-alarm-${severityTone(a.severity)}`}>
            <span className="wg-alarm-msg" title={a.code}>{a.message || a.code || '—'}</span>
            <span className="wg-alarm-time">{fmtTime(a.time)}</span>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

/* ---------------- 압력 ---------------- */
const fetchPressure = (id) => fetchMachineWidget(id, '압력');
function PressureWidget({ equipmentId, tr }) {
  const { data, state } = useWidgetData(fetchPressure, equipmentId);
  const rows = useMemo(() => [...(data?.data || [])].reverse(), [data]);
  return (
    <Frame state={state} tr={tr} empty={rows.length === 0}>
      <div className="wg-legend">
        <span><i style={{ background: C.primary }} />{tr('legendInjection')}</span>
        <span><i className="wg-legend-dashed" style={{ borderColor: C.accent }} />{tr('legendHolding')}</span>
      </div>
      <div className="wg-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 6, right: 4, bottom: 0, left: -24 }}>
            <XAxis dataKey="shotNumber" hide />
            <YAxis tick={TICK} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
            <Tooltip {...TOOLTIP} />
            <Line type="monotone" dataKey="injection" name={tr('legendInjection')} stroke={C.primary} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="holding" name={tr('legendHolding')} stroke={C.accent} strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Frame>
  );
}

/* ---------------- 사이클 ---------------- */
function CycleWidget({ equipmentId, tr }) {
  const { data, state } = useWidgetData(fetchMachineCycle, equipmentId);
  const stats = data?.cycleStatistics || null;
  const rows = useMemo(() => (data?.cycleTimeTrend?.data || []).slice(-12).filter((r) => isNum(r.cycleTime)), [data]);
  const maxRow = rows.reduce((m, r) => (m == null || Number(r.cycleTime) > Number(m.cycleTime) ? r : m), null);
  return (
    <Frame state={state} tr={tr} empty={!stats && rows.length === 0}>
      <div className="wg-stats">
        <div><span>{tr('avg')}</span><b>{num(stats?.averageCycle)}</b></div>   {/* cycleStatistics.averageCycle */}
        <div><span>{tr('min')}</span><b>{num(stats?.minCycle)}</b></div>       {/* cycleStatistics.minCycle */}
        <div><span>{tr('max')}</span><b>{num(stats?.maxCycle)}</b></div>       {/* cycleStatistics.maxCycle */}
      </div>
      <div className="wg-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 6, right: 4, bottom: 0, left: -28 }}>
            <XAxis dataKey="shotNumber" hide />
            <YAxis tick={TICK} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
            <Tooltip {...TOOLTIP} />
            <Bar dataKey="cycleTime" name={`${tr('cycleTime')} (s)`} radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {rows.map((r) => <Cell key={r.shotNumber} fill={r === maxRow ? C.primary : C.bar} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Frame>
  );
}

/* ---------------- SPC ---------------- */
const fetchSpcWidget = async (id) => {
  /* 쿠션량 SPC 설정이 없는 설비는 사이클타임으로 대체 */
  try { return { param: 'cushion', res: await fetchMachineSpc(id, 'cushion') }; } catch (e) { return { param: 'cycle_time', res: await fetchMachineSpc(id, 'cycle_time') }; }
};
function SpcWidget({ equipmentId, t, tr }) {
  const { data, state } = useWidgetData(fetchSpcWidget, equipmentId);
  const spc = data?.res || null;
  const rows = useMemo(() => [...((spc?.xbarChart || spc?.xBarChart)?.data || [])].reverse().map((d) => {
    const v = isNum(d.value) ? Number(d.value) : null;
    const ucl = isNum(d.ucl) ? Number(d.ucl) : null;
    const lcl = isNum(d.lcl) ? Number(d.lcl) : null;
    return { ...d, value: v, ucl, lcl, out: v != null && ucl != null && lcl != null && (v > ucl || v < lcl) };
  }), [spc]);
  const limits = rows[0] || {};
  const outCount = rows.filter((r) => r.out).length;
  const paramLabel = spc?.parameterLabel || t(`pages.monitoring.machine.spc.params.${data?.param === 'cushion' ? 'cushion' : 'cycleTime'}`);
  const unit = spc?.unit ? ` (${spc.unit})` : '';
  const dot = (p) => (p.payload?.out && isNum(p.cx) && isNum(p.cy) ? <circle key={p.index} cx={p.cx} cy={p.cy} r={3} fill={C.error} /> : <g key={p.index} />);
  return (
    <Frame state={state} tr={tr} empty={rows.length === 0}>
      <div className="wg-legend">
        <span><i style={{ background: C.primary }} />{tr('measured')}</span>
        <span><i className="wg-legend-dashed" style={{ borderColor: C.error }} />UCL {num(limits.ucl, 2)}</span>
        <span><i className="wg-legend-dashed" style={{ borderColor: C.accent }} />LCL {num(limits.lcl, 2)}</span>
        <span><i className="wg-legend-dot" style={{ background: C.error }} />{tr('out')} {outCount}</span>
        <span className="wg-legend-note">{tr('spcNote', { p: paramLabel })}{unit}</span>
      </div>
      <div className="wg-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 6, right: 4, bottom: 0, left: -24 }}>
            <XAxis dataKey="group" hide />
            <YAxis tick={TICK} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
            <Tooltip {...TOOLTIP} />
            {isNum(limits.ucl) && <ReferenceLine y={limits.ucl} stroke={C.error} strokeDasharray="4 3" label={{ value: 'UCL', position: 'insideTopRight', fill: C.error, fontSize: 10 }} />}
            {isNum(limits.cl) && <ReferenceLine y={Number(limits.cl)} stroke={C.tertiary} strokeDasharray="4 3" />}
            {isNum(limits.lcl) && <ReferenceLine y={limits.lcl} stroke={C.accent} strokeDasharray="4 3" label={{ value: 'LCL', position: 'insideBottomRight', fill: C.accent, fontSize: 10 }} />}
            <Line type="linear" dataKey="value" name={paramLabel} stroke={C.primary} strokeWidth={1.5} dot={dot} activeDot={{ r: 3 }} isAnimationActive={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Frame>
  );
}

/* ---------------- 양/불 ---------------- */
/* 오늘 집계 API(quality-summary)가 없는 백엔드(404)에서는 최근 100샷 표본으로 집계한다(개요 탭과 같은 방식, 어휘 GOOD/OK · BAD/DEFECT/NG) */
const QUALITY_SAMPLE_LIMIT = 100;
const fetchQuality = async (id) => {
  try {
    return await fetchMachineQualitySummary(id);
  } catch (e) {
    const sh = await fetchShotHistory(id, QUALITY_SAMPLE_LIMIT);
    let good = 0; let bad = 0;
    (sh?.shots || []).forEach((s) => {
      const q = (s.qualityStatus || '').toUpperCase();
      if (q === 'GOOD' || q === 'OK') good += 1;
      else if (q === 'BAD' || q === 'DEFECT' || q === 'NG') bad += 1;
    });
    return { goodCount: good, badCount: bad, totalShots: (sh?.shots || []).length, sample: true };
  }
};
function QualityWidget({ equipmentId, tr }) {
  const { data, state } = useWidgetData(fetchQuality, equipmentId);
  const good = Number(data?.goodCount) || 0;
  const bad = Number(data?.badCount) || 0;
  const judged = good + bad;
  const goodRate = judged ? (good / judged) * 100 : null;
  const defectRate = judged ? (bad / judged) * 100 : null;
  const r = 44; const c = 2 * Math.PI * r;
  return (
    <Frame state={state} tr={tr} empty={!data}>
      <div className="wg-quality">
        <div className="wg-donut-wrap">
          <svg viewBox="0 0 110 110" className="wg-donut" aria-hidden="true">
            <circle cx="55" cy="55" r={r} className="wg-donut-track" />
            <circle cx="55" cy="55" r={r} className="wg-donut-fill" style={{ strokeDasharray: `${((goodRate || 0) / 100) * c} ${c}` }} transform="rotate(-90 55 55)" />
          </svg>
          <div className="wg-donut-text">
            <b>{num(goodRate, 1)}<small>%</small></b>
            <span>{tr('goodRate')}{data?.sample ? ` · ${tr('sampleNote', { n: QUALITY_SAMPLE_LIMIT })}` : ''}</span>
          </div>
        </div>
        <dl className="wg-rows wg-rows-tight">
          <div><dt>{tr('good')}</dt><dd>{intFmt(good)}<small>shot</small></dd></div>                                   {/* quality-summary.goodCount */}
          <div><dt>{tr('defect')}</dt><dd className="wg-text-error">{intFmt(bad)}<small>shot</small></dd></div>        {/* quality-summary.badCount */}
          <div><dt>{tr('defectRate')}</dt><dd>{num(defectRate, 1)}<small>%</small></dd></div>
        </dl>
      </div>
    </Frame>
  );
}

/* ---------------- 기본사출정보 ---------------- */
const fetchInjection = (id) => fetchMachineWidget(id, '기본사출정보');
function InjectionInfoWidget({ equipmentId, t, tr }) {
  const { data, state } = useWidgetData(fetchInjection, equipmentId);
  const v = data?.injectionInfo || null;
  const L = (k) => t(`pages.monitoring.machine.overview.labels.${k}`);
  return (
    <Frame state={state} tr={tr} empty={!v}>
      <dl className="wg-rows wg-rows-tight">
        <div><dt>{L('maxPressure')}</dt><dd>{num(v?.maxPressure)}<small>MPa</small></dd></div>
        <div><dt>{L('startPosition')}</dt><dd>{num(v?.startPosition)}<small>mm</small></dd></div>
        <div><dt>{L('switchPosition')}</dt><dd>{num(v?.switchPosition)}<small>mm</small></dd></div>
        <div><dt>{L('injectionTime')}</dt><dd>{num(v?.time, 2)}<small>s</small></dd></div>
        <div><dt>{L('injectionSpeed')}</dt><dd>{num(v?.speed)}<small>mm/s</small></dd></div>
        <div><dt>{L('suckbackTime')}</dt><dd>{num(v?.suckbackTime, 2)}<small>s</small></dd></div>
        <div><dt>{L('suckbackSpeed')}</dt><dd>{num(v?.suckbackSpeed)}<small>mm/s</small></dd></div>
      </dl>
    </Frame>
  );
}

/* ---------------- 사용자 모듈 ---------------- */
const SERIES_COLORS = [C.primary, C.accent, C.success, C.warning, C.error];
const CUSTOM_UNIT = { pressure: 'MPa', temperature: '℃', speed: 'mm/s', position: 'mm' };
function CustomWidget({ widget, equipmentId, t, tr }) {
  const widgetId = widget.id;
  const yAxis = widget.config?.yAxis || 'temperature';
  /* 1순위: yAxis 로 조회(위젯 id 불필요). 백엔드 배포 전에는 id 로 조회하되, 저장 전 임시 id(Date.now)는 서버에 없으므로 건너뛴다 */
  const fetcher = useCallback(async (id) => {
    try {
      return await fetchCustomWidgetData(id, yAxis);
    } catch (e) {
      if (widgetId && Number(widgetId) < 1e11) return fetchCustomWidget(id, widgetId);
      throw e;
    }
  }, [yAxis, widgetId]);
  const { data, state } = useWidgetData(fetcher, equipmentId);
  const cfg = widget.config || null;
  const rows = useMemo(() => [...(data?.data || [])].reverse(), [data]);
  if (!cfg) return <Message text={t('pages.monitoring.machine.common.configError')} />;
  const xKey = cfg.xAxis || 'shotNumber';
  const L = (k) => t(`pages.monitoring.machine.legendName.${k}`);
  /* y축 종류별 계열 */
  const series = cfg.yAxis === 'pressure'
    ? [['injection', L('injectionPressure')], ['holding', L('holding')], ['metering', L('meteringPressure')], ['backPressure', L('backPressure')]]
    : cfg.yAxis === 'temperature'
      ? [1, 2, 3, 4, 5].map((i) => [`zone${i}`, `Zone ${i}`])
      : [[cfg.yAxis || 'speed', L(cfg.yAxis === 'position' ? 'position' : 'speed')]];
  const unit = CUSTOM_UNIT[cfg.yAxis] || '';
  const single = series.length === 1;
  /* 속도는 빌더 설정과 관계없이 꺾은선(사용자 요청). 위치만 빌더의 라인/막대/영역 설정을 따른다 */
  const chartType = single && cfg.yAxis !== 'speed' ? (cfg.chartType || 'line') : 'line';
  const common = { data: rows, margin: { top: 6, right: 4, bottom: 0, left: -24 } };
  const axes = (
    <>
      <XAxis dataKey={xKey} hide />
      <YAxis tick={TICK} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
      <Tooltip {...TOOLTIP} />
    </>
  );
  const legend = (
    <div className="wg-legend">
      {series.map(([key, name], i) => (
        <span key={key}><i style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }} />{name}</span>
      ))}
      {unit && <span className="wg-legend-note">{unit}</span>}
    </div>
  );
  return (
    <Frame state={state} tr={tr} empty={rows.length === 0}>
      {legend}
      <div className="wg-chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart {...common}>
              {axes}
              <Bar dataKey={series[0][0]} name={series[0][1]} fill={C.bar} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            </BarChart>
          ) : chartType === 'area' ? (
            <AreaChart {...common}>
              {axes}
              <Area type="monotone" dataKey={series[0][0]} name={series[0][1]} stroke={C.primary} fill={C.accent} fillOpacity={0.25} strokeWidth={1.5} isAnimationActive={false} connectNulls />
            </AreaChart>
          ) : (
            <LineChart {...common}>
              {axes}
              {series.map(([key, name], i) => (
                <Line key={key} type="monotone" dataKey={key} name={name} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </Frame>
  );
}

const BODIES = {
  '사용자모듈': CustomWidget,
  '기본정보': BasicInfoWidget,
  '온도': TemperatureWidget,
  '에너지': EnergyWidget,
  '알람이력': AlarmHistoryWidget,
  '압력': PressureWidget,
  '사이클': CycleWidget,
  'SPC': SpcWidget,
  '양불': QualityWidget,
  '기본사출정보': InjectionInfoWidget
};

/** 위젯 종류에 맞는 본문. 모르는 종류(사용자모듈 등)는 null 을 돌려주고 호출 쪽이 기존 renderWidget 을 쓴다 */
export function WidgetBody({ widget, equipmentId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.widget.body.${key}`, params), [t]);
  const Body = BODIES[widget.type];
  if (!Body) return null;
  return <Body widget={widget} equipmentId={equipmentId} t={t} tr={tr} size={widget.size} />;
}

export const hasWidgetBody = (type) => !!BODIES[type];
